import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useBookingRequests from '@/api/useBookingRequests.ts';
import useUpdateBookingStatus from '@/api/useUpdateBookingStatus.ts';
import dayjs from 'dayjs';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import { createColumnHelper } from '@tanstack/react-table';
import type { BookingRequest } from '@/api/useBookingRequests.ts';
import Routes from '@/router/Routes.ts';
import { exportAllBookingsToCalendar } from '@/utils/exportToCalendar.ts';
import { Button } from 'react-daisyui';
import { formatCareType } from '@/utils/formatCareType.ts';
import { trackEvent } from '@/utils/analytics.ts';
import DeclineBookingModal from '@/components/modals/DeclineBookingModal.tsx';

const columnHelper = createColumnHelper<BookingRequest>();

type TabKey = 'all' | 'pending' | 'accepted' | 'declined' | 'completed';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'declined', label: 'Declined' },
  { key: 'completed', label: 'Completed' },
];

const matchesTab = (status: string, tab: TabKey): boolean => {
  if (tab === 'all') return true;
  if (tab === 'completed') return status === 'completed' || status === 'not_completed';
  return status === tab;
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'accepted': return 'text-[#00A96E]';
    case 'pending':  return 'text-[#FFBE00]';
    case 'declined': return 'text-[#FF5861]';
    case 'completed': return 'text-[#00A96E]';
    case 'not_completed': return 'text-[#FF5861]';
    default: return 'text-gray-600';
  }
};

const getStatusLabel = (status: string) => {
  if (status === 'not_completed') return 'Not Completed';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const BookingsPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: bookings, isLoading, isError } = useBookingRequests();
  const { mutate: updateStatus, isPending: isUpdating } = useUpdateBookingStatus();
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [declineTarget, setDeclineTarget] = useState<BookingRequest | null>(null);

  const handleAccept = (bookingId: string) => {
    updateStatus(
      { booking_id: bookingId, status: 'accepted' },
      { onSuccess: () => trackEvent({ name: 'booking_request_accepted' }) }
    );
  };

  const handleDeclineConfirm = (reason: string) => {
    if (!declineTarget) return;
    updateStatus(
      { booking_id: declineTarget.id, status: 'declined', decline_reason: reason },
      { onSuccess: () => setDeclineTarget(null) }
    );
  };

  const handleShowDetails = (bookingId: string) => {
    navigate(Routes.BOOKING_DETAILS.replace(':bookingId', bookingId));
  };

  // Counts per tab (always based on full list)
  const tabCounts = useMemo(() => {
    if (!bookings) return {} as Record<TabKey, number>;
    return {
      all: bookings.length,
      pending: bookings.filter((b) => matchesTab(b.status, 'pending')).length,
      accepted: bookings.filter((b) => matchesTab(b.status, 'accepted')).length,
      declined: bookings.filter((b) => matchesTab(b.status, 'declined')).length,
      completed: bookings.filter((b) => matchesTab(b.status, 'completed')).length,
    };
  }, [bookings]);

  const filteredBookings = useMemo(
    () => (bookings || []).filter((b) => matchesTab(b.status, activeTab)),
    [bookings, activeTab]
  );

  const handleExportToCalendar = () => {
    const accepted = filteredBookings.filter((b) => b.status === 'accepted' && b.start_date);
    if (!accepted.length) {
      alert(t('bookingsPage.noAcceptedBookings'));
      return;
    }
    exportAllBookingsToCalendar(
      accepted.map((booking) => {
        const careTypeLabel = formatCareType(booking.care_type);
        const timeRange = booking.start_time && booking.end_time
          ? `${booking.start_time} - ${booking.end_time}`
          : booking.start_time || 'Time TBD';
        return {
          title: `${careTypeLabel} - ${booking.other_user_name}`,
          startDate: booking.start_date!,
          startTime: booking.start_time,
          endDate: booking.end_date,
          endTime: booking.end_time,
          description: [
            `Care Type: ${careTypeLabel}`,
            `Client: ${booking.other_user_name}`,
            booking.start_time && booking.end_time ? `Time: ${timeRange}` : '',
            booking.notes ? `\nNotes: ${booking.notes}` : '',
          ].filter(Boolean).join('\n'),
          location: 'To be determined',
        };
      })
    );
  };

  const showExport =
    (activeTab === 'accepted' || activeTab === 'all') &&
    filteredBookings.some((b) => b.status === 'accepted' && b.start_date);

  const columns = useMemo(
    () => [
      columnHelper.accessor('other_user_name', {
        header: 'Family',
        cell: (info) => (
          <span className="font-bold whitespace-nowrap">{info.row.original.other_user_name}</span>
        ),
      }),
      columnHelper.accessor('care_type', {
        header: 'Care Type',
        cell: (info) => (
          <span className="text-sm font-bold whitespace-nowrap">{formatCareType(info.getValue())}</span>
        ),
      }),
      columnHelper.accessor('start_date', {
        header: 'Date & Time',
        cell: (info) => {
          const booking = info.row.original;
          const timeRange =
            booking.start_time && booking.end_time
              ? `${booking.start_time} - ${booking.end_time}`
              : booking.start_time || '';
          return (
            <span className="text-sm whitespace-nowrap">
              {booking.start_date
                ? `${dayjs(booking.start_date).format('MMM DD, YYYY')} — ${timeRange}`
                : 'Not specified'}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'details',
        header: 'Details',
        cell: (info) => (
          <button
            onClick={() => handleShowDetails(info.row.original.id)}
            className="text-black hover:opacity-70 text-sm font-bold cursor-pointer"
          >
            Show details
          </button>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Action',
        cell: (info) => {
          const booking = info.row.original;
          if (!booking.is_requester && booking.status === 'pending') {
            return (
              <div className="flex gap-4">
                <button
                  onClick={() => handleAccept(booking.id)}
                  disabled={isUpdating}
                  className="text-[#00A96E] hover:opacity-70 font-bold disabled:opacity-50 cursor-pointer"
                >
                  Accept
                </button>
                <span className="text-gray-300 font-bold">/</span>
                <button
                  onClick={() => setDeclineTarget(booking)}
                  disabled={isUpdating}
                  className="text-[#FF5861] hover:opacity-70 font-bold disabled:opacity-50 cursor-pointer"
                >
                  Decline
                </button>
              </div>
            );
          }
          return (
            <div className="flex flex-col gap-0.5">
              <span className={`font-semibold ${getStatusColor(booking.status)}`}>
                {getStatusLabel(booking.status)}
              </span>
              {booking.status === 'declined' && booking.decline_reason && (
                <span className="text-xs text-gray-400">{booking.decline_reason}</span>
              )}
            </div>
          );
        },
      }),
    ],
    [isUpdating]
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 w-full">
        <div className="text-xl font-semibold">{t('bookingsPage.title')}</div>
        <div className="flex gap-2">
          {TABS.map((tab) => (
            <div key={tab.key} className="h-9 w-24 bg-gray-200 rounded-full animate-pulse" />
          ))}
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-12 bg-gray-200 rounded-lg" />
          <div className="h-12 bg-gray-200 rounded-lg" />
          <div className="h-12 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-6 w-full">
        <div className="text-xl font-semibold">{t('bookingsPage.title')}</div>
        <div className="text-red-500">{t('bookingsPage.errorLoading')}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
        <div>
          <h1 className="text-2xl font-bold">{t('bookingsPage.title')}</h1>
          <p className="text-gray-600 text-sm mt-1">{t('bookingsPage.subtitle')}</p>
        </div>
        {showExport && (
          <Button
            onClick={handleExportToCalendar}
            className="bg-black text-white border-none hover:opacity-80 rounded-[12px] px-6 w-full sm:w-auto"
          >
            {t('bookingsPage.exportToCalendar')}
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {TABS.map((tab) => {
          const count = tabCounts[tab.key] ?? 0;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer ${
                isActive
                  ? 'bg-black text-white'
                  : 'bg-[#F6F6F4] text-gray-600 hover:bg-gray-200'
              }`}
            >
              {tab.label}
              {count > 0 && (
                <span
                  className={`text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center ${
                    isActive ? 'bg-white text-black' : 'bg-gray-300 text-gray-700'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Table or empty state */}
      {filteredBookings.length === 0 ? (
        <div className="bg-[#F6F6F4] rounded-lg py-10 flex items-center justify-center w-full">
          <p className="text-base font-semibold text-center text-black">
            {activeTab === 'all'
              ? t('bookingsPage.noBookings')
              : `No ${activeTab} bookings`}
          </p>
        </div>
      ) : (
        <div className="w-full overflow-x-auto">
          <DataTable
            data={filteredBookings}
            columns={columns}
            wrapperClassName="border border-gray-200 rounded-lg min-w-[600px]"
            rowClassName={(index: number) => (index % 2 === 0 ? 'bg-[#F6F6F4]' : 'bg-white')}
          />
        </div>
      )}

      <DeclineBookingModal
        isOpen={!!declineTarget}
        onClose={() => setDeclineTarget(null)}
        onConfirm={handleDeclineConfirm}
        familyName={declineTarget?.other_user_name ?? ''}
        isLoading={isUpdating}
      />
    </div>
  );
};

export default BookingsPage;
